"use client";

import { useEffect, useMemo, useState, type ReactNode } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { NAV_ITEMS } from "@/constants/nav-items";

function normalizeHref(href: string) {
  if (!href) return "/";
  const trimmed = href.trim();
  if (trimmed === "/") return "/";
  return trimmed.replace(/\/+$/, "");
}

function makeSafeId(input: string) {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function getNavIcon(routeKey: string, gradient: ReactNode, strokeUrl: string) {
  const key = normalizeHref(routeKey);

  const SaleIcon = (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {gradient}
      <rect x="3" y="5" width="18" height="15" rx="2" stroke={strokeUrl} strokeWidth="2" />
      <path d="M3 9h18" stroke={strokeUrl} strokeWidth="2" />
      <path d="M7 3h10v2H7z" fill={strokeUrl} opacity="0.5" />
      <path d="M8 13h8v4H8z" stroke={strokeUrl} strokeWidth="1.8" />
      <circle cx="12" cy="10" r="1.2" fill={strokeUrl} />
    </svg>
  );

  const CashIcon = (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {gradient}
      <rect x="3" y="6" width="18" height="12" rx="2" stroke={strokeUrl} strokeWidth="2" />
      <rect x="6" y="9" width="12" height="3" rx="0.5" stroke={strokeUrl} strokeWidth="1.8" />
      <circle cx="12" cy="15" r="1.2" fill={strokeUrl} />
      <path d="M9 5h6l1 1v1H8V6z" fill={strokeUrl} opacity="0.7" />
      <path d="M10 17h4v2h-4z" stroke={strokeUrl} strokeWidth="1.6" />
    </svg>
  );

  const ReportsIcon = (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {gradient}
      <path d="M3 3v18h18" stroke={strokeUrl} strokeWidth="2" strokeLinecap="round" />
      <rect x="7" y="10" width="2" height="6" fill={strokeUrl} />
      <rect x="11" y="7" width="2" height="9" fill={strokeUrl} />
      <rect x="15" y="5" width="2" height="11" fill={strokeUrl} />
      <path d="M7 10l4-3 4 3 4-4" stroke={strokeUrl} strokeWidth="1.5" />
    </svg>
  );

  const WarehouseIcon = (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {gradient}
      <path
        d="M3 7l9-4 9 4v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
        stroke={strokeUrl}
        strokeWidth="2"
      />
      <rect x="7" y="10" width="4" height="3" rx="0.5" stroke={strokeUrl} strokeWidth="1.6" />
      <rect x="13" y="10" width="4" height="3" rx="0.5" stroke={strokeUrl} strokeWidth="1.6" />
      <path d="M4 17h16" stroke={strokeUrl} strokeWidth="1.5" />
    </svg>
  );

  const ProductsIcon = (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {gradient}
      <path d="M12 2l9 4-9 4-9-4 9-4z" stroke={strokeUrl} strokeWidth="2" />
      <path d="M3 6v6l9 4 9-4V6" stroke={strokeUrl} strokeWidth="2" />
      <path d="M7 10l5 2 5-2" stroke={strokeUrl} strokeWidth="1.6" />
    </svg>
  );

  const SuppliersIcon = (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {gradient}
      <circle cx="8" cy="7" r="2.5" stroke={strokeUrl} strokeWidth="2" />
      <circle cx="16" cy="7" r="2.5" stroke={strokeUrl} strokeWidth="2" />
      <circle cx="12" cy="16" r="2.8" stroke={strokeUrl} strokeWidth="2" />
      <path d="M8 10l4 3 4-3" stroke={strokeUrl} strokeWidth="1.8" />
    </svg>
  );

  const SettingsIcon = (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {gradient}
      <circle cx="12" cy="12" r="3" stroke={strokeUrl} strokeWidth="2" />
      <path
        d="M19 13a7 7 0 00-.4 2.3l1.6 1.2-1.6 2.8-1.9-.8A7 7 0 0014 20l-.3 2h-3.4L10 20a7 7 0 00-2.7-.5l-1.9.8-1.6-2.8 1.6-1.2A7 7 0 005 13l-2-.3V9.7L5 9a7 7 0 00.4-2.3L3.8 5.5 5.4 2.7l1.9.8A7 7 0 0010 4l.3-2h3.4L14 4a7 7 0 002.7.5l1.9-.8 1.6 2.8-1.6 1.2A7 7 0 0019 11l2 .3v3.4z"
        stroke={strokeUrl}
        strokeWidth="1.5"
      />
    </svg>
  );

  const DefaultIcon = (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {gradient}
      <path
        d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
        stroke={strokeUrl}
        strokeWidth="2"
      />
      <path d="M9 22V12h6v10" stroke={strokeUrl} strokeWidth="2" />
    </svg>
  );

  switch (key) {
    case "/venta":
    case "/sales":
    case "/point-of-sale":
      return SaleIcon;
    case "/caja":
    case "/cash":
      return CashIcon;
    case "/reportes":
    case "/reports":
      return ReportsIcon;
    case "/almacen":
    case "/almacenes":
    case "/warehouse":
      return WarehouseIcon;
    case "/productos":
    case "/products":
      return ProductsIcon;
    case "/proveedores":
    case "/suppliers":
      return SuppliersIcon;
    case "/config":
    case "/configuracion":
    case "/configuration":
      return SettingsIcon;
    default:
      return DefaultIcon;
  }
}

function MobileBottomBar() {
  const pathnameRaw = usePathname();
  const pathname = useMemo(() => normalizeHref(pathnameRaw || "/"), [pathnameRaw]);

  const isActive = (href: string) => {
    const target = normalizeHref(href);
    if (target === "/") return pathname === "/";
    return pathname === target || pathname.startsWith(`${target}/`);
  };

  const items = useMemo(() => NAV_ITEMS.filter((i) => i.href !== "/"), []);

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50
      border-t border-gray-200/30 dark:border-gray-700/40
      bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl
      lg:hidden [@media(pointer:coarse)]:block"
      role="navigation"
      aria-label="Bottom Navigation"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="relative px-2 py-3">
        <div className="pointer-events-none absolute inset-x-0 -top-6 h-6 bg-gradient-to-t from-blue-500/10 to-transparent dark:from-blue-400/10" />
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-white/95 to-transparent dark:from-slate-900/95" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-white/95 to-transparent dark:from-slate-900/95" />

        {/* Key: scroll on small screens, fill width on larger ones */}
        <div
          className="no-scrollbar flex items-stretch gap-2 overflow-x-auto snap-x snap-mandatory lg:overflow-x-hidden"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {items.map((item) => {
            const active = isActive(item.href);

            const gradId = `mb-grad-${makeSafeId(item.href || item.label || "item")}`;
            const strokeUrl = `url(#${gradId})`;

            const gradient = (
              <defs>
                <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3B82F6" />
                  <stop offset="100%" stopColor="#A855F7" />
                </linearGradient>
              </defs>
            );

            const icon = getNavIcon(item.href, gradient, strokeUrl);

            return (
              <Link
                key={item.href}
                href={item.href}
                aria-label={item.label}
                title={item.label}
                aria-current={active ? "page" : undefined}
                className={[
                  "snap-start",
                  // Fill the bar on wider devices (no right gap)
                  "flex-1 basis-0",
                  // Keep scroll behavior on smaller screens
                  "shrink-0 lg:shrink",
                  // Clamp width: min width for mobile, no min width on lg+ so it can fill evenly
                  "min-w-[92px] sm:min-w-[104px] md:min-w-[112px] lg:min-w-0",
                  "max-w-[180px] lg:max-w-none",
                  "relative flex flex-col items-center justify-center",
                  "rounded-2xl px-4 py-3",
                  "transition-all duration-200",
                  "active:scale-95",
                  active
                    ? "bg-gradient-to-r from-blue-100/85 to-purple-100/85 dark:from-blue-900/35 dark:to-purple-900/35"
                    : "hover:bg-gray-100/70 dark:hover:bg-gray-800/60",
                ].join(" ")}
              >
                <div
                  className={[
                    "absolute top-1 h-1.5 w-10 rounded-full",
                    "transition-opacity duration-300",
                    active ? "opacity-100" : "opacity-0",
                    "bg-gradient-to-r from-blue-500/70 to-purple-500/70",
                  ].join(" ")}
                />

                <div className={`transition-transform duration-200 ${active ? "scale-[1.2]" : "scale-[1.12]"}`}>
                  {icon}
                </div>

                <span
                  className={[
                    "mt-2 w-full truncate text-center",
                    "text-[12.5px] leading-none",
                    active
                      ? "text-blue-800 dark:text-blue-300 font-semibold"
                      : "text-gray-700 dark:text-gray-300",
                  ].join(" ")}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

export default function ModernSidebar() {
  const pathnameRaw = usePathname();
  const pathname = useMemo(() => normalizeHref(pathnameRaw || "/"), [pathnameRaw]);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const isActive = (href: string) => {
    const target = normalizeHref(href);
    if (target === "/") return pathname === "/";
    return pathname === target || pathname.startsWith(`${target}/`);
  };

  useEffect(() => {
    try {
      const savedCollapsed = localStorage.getItem("posmod.sidebar.collapsed");
      const savedFirstLoad = localStorage.getItem("posmod.sidebar.firstLoad");
      if (savedCollapsed) setIsCollapsed(savedCollapsed === "1");
      setIsFirstLoad(savedFirstLoad === null);
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("posmod.sidebar.collapsed", isCollapsed ? "1" : "0");
    } catch {}
  }, [isCollapsed]);

  useEffect(() => {
    if (isFirstLoad && !isCollapsed) {
      const timer = setTimeout(() => {
        setIsCollapsed(true);
        try {
          localStorage.setItem("posmod.sidebar.firstLoad", "false");
          localStorage.setItem("posmod.sidebar.collapsed", "1");
        } catch {}
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [isFirstLoad, isCollapsed]);

  return (
    <>
      <aside
        className={`hidden lg:flex [@media(pointer:coarse)]:hidden sticky top-0 h-screen border-r border-gray-200/20 dark:border-gray-700/40
        bg-gradient-to-b from-white/95 via-blue-50/40 to-purple-50/40
        dark:from-slate-900 dark:via-slate-800 dark:to-slate-800
        backdrop-blur-xl shadow-2xl shadow-blue-500/15
        flex-col transition-all duration-700 ease-in-out
        ${isCollapsed ? "w-20" : "w-72"} overflow-x-hidden overflow-y-auto`}
      >
        <div
          className={`h-14 flex items-center border-b border-gray-200/30 dark:border-gray-700/40
          transition-all duration-500 ${isCollapsed ? "justify-center" : "justify-between px-5"}`}
        >
          {!isCollapsed && (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 dark:from-indigo-400 dark:to-fuchsia-500 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-extrabold text-lg">P</span>
              </div>
              <span className="text-2xl font-extrabold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-indigo-400 dark:to-pink-500 bg-clip-text text-transparent">
                POS MOD
              </span>
            </div>
          )}

          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`p-2 rounded-full transition-all duration-500 hover:bg-blue-100/50 dark:hover:bg-blue-900/40 ${
              isCollapsed ? "mx-auto" : "ml-3"
            }`}
            aria-label="Toggle Sidebar"
            title="Toggle Sidebar"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={`transition-transform duration-700 ${isCollapsed ? "rotate-180" : ""}`}
            >
              <defs>
                <linearGradient id="toggleGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#3B82F6" />
                  <stop offset="100%" stopColor="#A855F7" />
                </linearGradient>
              </defs>
              <path
                d={isCollapsed ? "M8 18L16 12L8 6" : "M16 18L8 12L16 6"}
                stroke="url(#toggleGrad)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <nav className={`flex-1 pb-8 space-y-2 mt-6 ${isCollapsed ? "px-3" : "px-4"}`}>
          {NAV_ITEMS.filter((item) => item.href !== "/").map((item) => {
            const active = isActive(item.href);
            const hovered = hoveredItem === item.href;

            const gradId = `sb-grad-${makeSafeId(item.href || item.label || "item")}`;
            const strokeUrl = `url(#${gradId})`;

            const gradient = (
              <defs>
                <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3B82F6" />
                  <stop offset="100%" stopColor="#A855F7" />
                </linearGradient>
              </defs>
            );

            const icon = getNavIcon(item.href, gradient, strokeUrl);

            return (
              <Link
                key={item.href}
                href={item.href}
                onMouseEnter={() => setHoveredItem(item.href)}
                onMouseLeave={() => setHoveredItem(null)}
                onFocus={() => setHoveredItem(item.href)}
                onBlur={() => setHoveredItem(null)}
                aria-label={item.label}
                title={item.label}
                aria-current={active ? "page" : undefined}
                className={`group flex items-center rounded-3xl py-3 font-semibold transition-all duration-500 relative overflow-hidden
                  ${isCollapsed ? "justify-center px-0" : "justify-start gap-4 px-5"}
                  ${
                    active
                      ? "bg-gradient-to-r from-blue-100/80 to-purple-100/80 dark:from-blue-900/30 dark:to-purple-900/30 text-blue-800 dark:text-blue-300 shadow-md border border-blue-200/50 dark:border-blue-800/40"
                      : "text-gray-700 dark:text-gray-300 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-gray-100/70 dark:hover:bg-gray-800/60"
                  }
                  ${hovered ? "transform scale-[1.04]" : ""}`}
              >
                {active && (
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 dark:from-blue-700/10 dark:to-purple-700/10 rounded-3xl animate-pulse" />
                )}

                <div className={`relative z-10 transition-transform duration-500 ${hovered ? "scale-125 brightness-125" : ""}`}>
                  {icon}
                </div>

                {!isCollapsed && (
                  <span className="relative z-10 truncate whitespace-nowrap transition-all duration-500 drop-shadow-md">
                    {item.label}
                  </span>
                )}

                {isCollapsed && (
                  <>
                    <span className="sr-only">{item.label}</span>
                    <div
                      className="pointer-events-none absolute left-full ml-3 px-3 py-1.5 bg-gray-900/90 dark:bg-gray-800/90 text-white text-sm rounded-lg opacity-0
                      group-hover:opacity-100 group-focus-visible:opacity-100
                      transition-all duration-300 shadow-lg z-50 transform
                      group-hover:translate-x-1 group-focus-visible:translate-x-1"
                    >
                      {item.label}
                    </div>
                  </>
                )}
              </Link>
            );
          })}
        </nav>
      </aside>

      <MobileBottomBar />
    </>
  );
}
